import { isNullOrWhiteSpace, join, split, printf, toText, trimEnd, substring, isNullOrEmpty } from "./fable_modules/fable-library-js.4.29.0/String.js";
import { nb } from "date-fns/locale";
import { format as format_2 } from "date-fns";
import { op_Subtraction, now, date as date_2 } from "./fable_modules/fable-library-js.4.29.0/Date.js";
import { totalHours as totalHours_1, totalMinutes, totalDays } from "./fable_modules/fable-library-js.4.29.0/TimeSpan.js";
import { max } from "./fable_modules/fable-library-js.4.29.0/Double.js";
import { createElement } from "react";
import React from "react";
import { defaultOf, comparePrimitives, int32ToString, createObj } from "./fable_modules/fable-library-js.4.29.0/Util.js";
import { ShieldLockRegular, WeatherSunnyLowRegular } from "@fluentui/react-icons";
import { useComboboxFilter, Combobox, Text as Text$, Avatar, Display, Caption1, tokens } from "@fluentui/react-components";
import { reactApi } from "./fable_modules/Feliz.2.9.0/Interop.fs.js";
import { toArray as toArray_1, singleton, ofArray } from "./fable_modules/fable-library-js.4.29.0/List.js";
import { FSharpRef, Record } from "./fable_modules/fable-library-js.4.29.0/Types.js";
import { record_type, bool_type, string_type } from "./fable_modules/fable-library-js.4.29.0/Reflection.js";
import { iterate, delay, toList } from "./fable_modules/fable-library-js.4.29.0/Seq.js";
import { tail, item, equalsWith, head, tryFind, sortBy, map } from "./fable_modules/fable-library-js.4.29.0/Array.js";
import { map as map_1, bind, toArray, defaultArg } from "./fable_modules/fable-library-js.4.29.0/Option.js";
import { reactApi as reactApi_1 } from "./fable_modules/Feliz.2.9.0/Interop.fs.js";
import { tryParse } from "./fable_modules/fable-library-js.4.29.0/Int32.js";
import { defaultDatePickerStrings } from "@fluentui/react-datepicker-compat";
import { CalendarStrings } from "./fable_modules/FS.FluentUI.3.0.0/Domain.fs.js";

/**
 * Capitalise the first character; leaves the rest unchanged. Useful after
 * `Format("EEEE", …)` since the locale returns lowercase weekday names.
 */
export function capitalizeFirst(s) {
    if (isNullOrEmpty(s)) {
        return s;
    }
    else {
        return s[0].toLocaleUpperCase() + substring(s, 1);
    }
}

/**
 * Three-letter uppercase weekday: `MAN`, `TIR`, …
 */
export function weekdayShortNb(d) {
    let locale, formatOptions;
    return trimEnd((locale = nb, (formatOptions = {}, (formatOptions.locale = locale, format_2(d, "EEE", formatOptions)))), ".").toLocaleUpperCase();
}

/**
 * Full weekday, capitalised first letter: `Mandag`, `Tirsdag`, …
 */
export function weekdayLongNb(d) {
    let locale, formatOptions;
    return capitalizeFirst(trimEnd((locale = nb, (formatOptions = {}, (formatOptions.locale = locale, format_2(d, "EEEE", formatOptions)))), "."));
}

/**
 * Three-letter lowercase month: `mai`, `des`, …
 */
export function monthShortNb(d) {
    let locale, formatOptions;
    return trimEnd((locale = nb, (formatOptions = {}, (formatOptions.locale = locale, format_2(d, "MMM", formatOptions)))), ".").toLocaleLowerCase();
}

/**
 * Capitalised relative day, used in event-detail contexts:
 * <0  → "Avsluttet"
 * 0   → "I dag"
 * 1   → "I morgen"
 * ≤7  → "Om N dager"
 * else absolute "d. MMM yyyy"
 */
export function relativeDayLabel(d) {
    const today = date_2(now());
    const diff = ~~totalDays(op_Subtraction(date_2(d), today)) | 0;
    if (diff < 0) {
        return "Avsluttet";
    }
    else {
        switch (diff) {
            case 0:
                return "I dag";
            case 1:
                return "I morgen";
            default:
                if (diff <= 7) {
                    return `Om ${diff} dager`;
                }
                else {
                    const locale = nb;
                    const formatOptions = {};
                    formatOptions.locale = locale;
                    return format_2(d, "d. MMM yyyy", formatOptions);
                }
        }
    }
}

/**
 * Lowercase relative day with weeks/months bucket — matches the
 * upcoming-events design spec:
 * ≤0  → "i dag"
 * 1   → "i morgen"
 * <7  → "om N dager"
 * <14 → "om 1 uke"
 * <30 → "om N uker"
 * ≥30 → "om N mnd"
 */
export function relativeWhen(d) {
    const today = date_2(now());
    const diff = ~~totalDays(op_Subtraction(date_2(d), today)) | 0;
    if (diff <= 0) {
        return "i dag";
    }
    else if (diff === 1) {
        return "i morgen";
    }
    else if (diff < 7) {
        return `om ${diff} dager`;
    }
    else if (diff < 14) {
        return "om 1 uke";
    }
    else if (diff < 30) {
        return `om ${~~((diff + 3) / 7)} uker`;
    }
    else {
        return `om ${~~((diff + 15) / 30)} mnd`;
    }
}

/**
 * "Mandag 18. mai 2026, 14:30" — the long-form starter line used in
 * event detail / event-view dialog headers.
 */
export function formatStarter(d) {
    let locale, formatOptions, locale_1, formatOptions_1;
    return `${weekdayLongNb(d)} ${(locale = nb, (formatOptions = {}, (formatOptions.locale = locale, format_2(d, "d. MMMM yyyy", formatOptions))))}, ${(locale_1 = nb, (formatOptions_1 = {}, (formatOptions_1.locale = locale_1, format_2(d, "HH:mm", formatOptions_1))))}`;
}

/**
 * Compact duration between two timestamps: "45 min" / "2 timer" /
 * "1 t 30 m" / "3 dager" / "1 d 4 t". Negative spans clamp to 0.
 */
export function formatDuration(start, endt) {
    const totalMin = max(0, ~~totalMinutes(op_Subtraction(endt, start))) | 0;
    const totalHours = ~~(totalMin / 60) | 0;
    const mins = (totalMin % 60) | 0;
    if (totalHours >= 24) {
        const days = ~~(totalHours / 24) | 0;
        const remH = (totalHours % 24) | 0;
        if (remH === 0) {
            return `${days} dager`;
        }
        else {
            return `${days} d ${remH} t`;
        }
    }
    else if ((totalHours > 0) && (mins > 0)) {
        return `${totalHours} t ${mins} m`;
    }
    else if (totalHours > 0) {
        return `${totalHours} timer`;
    }
    else {
        return `${mins} min`;
    }
}

/**
 * Takes a string that is directly injected into the DOM
 */
export function injectCss(css) {
    const style = document.createElement("style");
    style.innerText = css;
    document.head.appendChild(style);
}

/**
 * Takes an int and generates a deterministic color
 */
export function intToColor(input) {
    const r = ((input * 123) % 256) | 0;
    const g = ((input * 321) % 256) | 0;
    const b = ((input * 213) % 256) | 0;
    return toText(printf("#%02X%02X%02X"))(r)(g)(b);
}

export function dataFetchFailedView() {
    let elems, el, el_2;
    return createElement("div", createObj(ofArray([["style", {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    }], (elems = [(el = WeatherSunnyLowRegular, createElement(el, {
        primaryFill: tokens.colorBrandForeground1,
        style: {
            fontSize: 96 + "px",
        },
    })), (el_2 = Caption1, createElement(el_2, {
        children: "Noe gikk galt, klarte ikke å hente data",
    }))], ["children", reactApi.Children.toArray(Array.from(elems))])])));
}

export function unauthorizedView() {
    let elems, el, el_2;
    return createElement("div", createObj(ofArray([["style", {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    }], (elems = [(el = ShieldLockRegular, createElement(el, {
        primaryFill: tokens.colorBrandForeground1,
        style: {
            fontSize: 96 + "px",
        },
    })), (el_2 = Caption1, createElement(el_2, {
        children: "Du har ikke de nødvendige tilgangene for dette innholdet",
    }))], ["children", reactApi.Children.toArray(Array.from(elems))])])));
}

export function notFoundView() {
    let elems, el, el_2;
    return createElement("div", createObj(ofArray([["style", {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    }], (elems = [(el = Display, createElement(el, {
        children: "404",
    })), (el_2 = Caption1, createElement(el_2, {
        children: "Siden du lette etter finnes ikke",
    }))], ["children", reactApi.Children.toArray(Array.from(elems))])])));
}

class ComboboxOption$1 extends Record {
    constructor(children, value, disabled) {
        super();
        this.children = children;
        this.value = value;
        this.disabled = disabled;
    }
}

function ComboboxOption$1_$reflection(gen0) {
    return record_type("Helpers.ComboboxOption`1", [gen0], ComboboxOption$1, () => [["children", gen0], ["value", string_type], ["disabled", bool_type]]);
}

/**
 * Combobox for selecting a user
 */
export function UserSearchBox(userSearchBoxInputProps) {
    let elems_1, config, el_4, options_1_1, config_1;
    const callback = userSearchBoxInputProps.callback;
    const users = userSearchBoxInputProps.users;
    const patternInput = reactApi.useState("");
    const setQuery = patternInput[1];
    const query = patternInput[0];
    const options = toList(delay(() => map((user_1) => {
        let elems, el, el_2;
        return new ComboboxOption$1(singleton(createElement("div", createObj(ofArray([["key", `${user_1.Id}`], ["style", {
            display: "flex",
            gap: 5 + "px ",
            alignItems: "center",
        }], (elems = [(el = Avatar, createElement(el, {
            color: "colorful",
            image: {
                src: defaultArg(user_1.ThumbnailUrl, ""),
            },
            name: `${user_1.FirstName} ${user_1.LastName}`,
        })), (el_2 = Text$, createElement(el_2, {
            children: `${user_1.FirstName} ${user_1.LastName}`,
        }))], ["children", reactApi.Children.toArray(Array.from(elems))])])))), int32ToString(user_1.Id), false);
    }, sortBy((user) => (`${user.FirstName} ${user.LastName}`), users, {
        Compare: comparePrimitives,
    }))));
    const el_6 = Combobox;
    return createElement(el_6, createObj(ofArray([["placeholder", "Søk etter bruker"], ["value", query], ["onChange", (ev) => {
        setQuery(ev.target.value);
    }], ["onOptionSelect", (_arg, value_1_6) => {
        iterate((arg) => {
            const unitVar = callback(arg);
            setQuery("");
        }, toArray(bind((userId) => tryFind((user_2) => (int32ToString(user_2.Id) === userId), users), value_1_6.optionValue)));
    }], (elems_1 = ((config = ofArray([["noOptionsMessage", (el_4 = Text$, createElement(el_4, {
        children: "Ingen brukere passer søket",
    }))], ["filter", (delegateArg, delegateArg_1) => {
        const input = delegateArg_1;
        return defaultArg(map_1((user_4) => {
            if (user_4.FirstName.toLocaleLowerCase().indexOf(input.toLocaleLowerCase()) >= 0) {
                return true;
            }
            else {
                return user_4.LastName.toLocaleLowerCase().indexOf(input.toLocaleLowerCase()) >= 0;
            }
        }, tryFind((user_3) => (int32ToString(user_3.Id) === delegateArg), users)), false);
    }]]), (options_1_1 = toArray_1(options), (config_1 = createObj(config), useComboboxFilter([query, options_1_1, config_1][0], [query, options_1_1, config_1][1], [query, options_1_1, config_1][2]))))), ["children", reactApi_1.Children.toArray(Array.from(elems_1))])])));
}

export function formatTimeDiff(ts) {
    if (totalMinutes(ts) < 1) {
        return "nå";
    }
    else if (totalHours_1(ts) < 1) {
        return `${~~totalMinutes(ts)} min siden`;
    }
    else if (totalDays(ts) < 1) {
        return `${~~totalHours_1(ts)} timer siden`;
    }
    else {
        return `${~~totalDays(ts)} dager siden`;
    }
}

export function tryParseInt(s) {
    let matchValue;
    let outArg = 0;
    matchValue = [tryParse(s, 511, false, 32, new FSharpRef(() => outArg, (v) => {
        outArg = (v | 0);
    })), outArg];
    if (matchValue[0]) {
        return matchValue[1];
    }
    else {
        return undefined;
    }
}

/**
 * Abbreviate a name by truncating middle names to their initials.
 * Also takes into account hyphen-separated middle names.
 * Keeps the first given, and the last name intact.
 */
export function abbreviateName(firstName, lastName) {
    let array_2;
    const givenNames = split(firstName, [" "], undefined, 0);
    const first = head(givenNames);
    const withInitials = `${first} ${join(" ", map((n) => {
        const matchValue_2 = split(n, ["-"], undefined, 0);
        if (!equalsWith((x, y) => (x === y), matchValue_2, defaultOf()) && (matchValue_2.length === 1)) {
            const singleName = item(0, matchValue_2);
            return `${singleName.toLocaleUpperCase()[0]}.`;
        }
        else {
            return join("-", map((h) => (`${h.toLocaleUpperCase()[0]}.`), matchValue_2.filter((arg_1) => !isNullOrWhiteSpace(arg_1))));
        }
    }, (array_2 = tail(givenNames), array_2.filter((arg) => !isNullOrWhiteSpace(arg)))))} ${lastName}`;
    if (withInitials.length <= 20) {
        return withInitials;
    }
    else {
        return `${first} ${lastName}`;
    }
}

export const datePickerStrings = new CalendarStrings("Gå til i dag", "Forrige måned", "Neste måned", "Forrige år", "Neste år", defaultDatePickerStrings.prevYearRangeAriaLabel, defaultDatePickerStrings.nextYearRangeAriaLabel, defaultDatePickerStrings.monthPickerHeaderAriaLabel, defaultDatePickerStrings.yearPickerHeaderAriaLabel, "Lukk", "Uke {0}", defaultDatePickerStrings.selectedDateFormatString, defaultDatePickerStrings.todayDateFormatString, defaultDatePickerStrings.dayMarkedAriaLabel, ["Januar", "Februar", "Mars", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Desember"], ["Jan", "Feb", "Mar", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Des"], ["Søndag", "Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "Lørdag"], ["Sø", "Ma", "Ti", "On", "To", "Fr", "Lø"]);

//# sourceMappingURL=Helpers.jsx.map
